import { useQuery } from '@tanstack/react-query';

import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { PaginatedResponse, ParamsWithPagination } from '@/types/api';

import { AudienceFilters, TAudience } from '../types';

import { audiencesQueryKeys } from './query-keys';

export async function getAudiences(
  params?: ParamsWithPagination<AudienceFilters>
): Promise<PaginatedResponse<TAudience>> {
  return mediaManagerClient.get('api/v1/audiences', {
    params: {
      limit: params?.limit,
      offset: params?.offset,
      search: params?.search,
      country__in: params?.countryIn?.join(','),
      deleted_at__isnull: params?.deletedAtIsNull,
      end_datetime__gte: params?.endDatetimeGte,
      end_datetime__lte: params?.endDatetimeLte,
      id__in: params?.idIn?.join(','),
      channel_source_id__in: params?.channelSourceIdIn?.join(','),
      media_type__in: params?.mediaTypeIn?.join(','),
      name: params?.name,
      /**
       * Omitted when nothing is requested: `provider__in=` is a filter on the empty
       * provider — a query that matches nothing — and not the absence of a filter.
       */
      provider__in: params?.providerIn?.length ? params.providerIn.join(',') : undefined,
      region__in: params?.regionIn?.join(','),
      scope__in: params?.scopeIn?.join(','),
      start_datetime__gte: params?.startDatetimeGte,
      start_datetime__lte: params?.startDatetimeLte,
      program_source_id__isnull: params?.programSourceIdIsnull,
    },
  });
}

type QueryFnType = typeof getAudiences;

interface UseAudienceParams {
  config?: QueryConfig<QueryFnType>;
  params?: ParamsWithPagination<AudienceFilters>;
}

export function useAudiences({ config, params }: UseAudienceParams = {}) {
  return useQuery({
    ...config,
    queryKey: audiencesQueryKeys.list(params),
    queryFn: () => getAudiences(params),
  });
}
